import { useEffect, useState } from "react";

import { Fingerprint, Loader2, ScanFace } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { GetRequest } from "@/lib/http";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import Unavailable from "../Unavailable";
import { NativeBiometric } from "@capgo/capacitor-native-biometric";
import { useSelector } from "react-redux";
import { Button } from "../ui/button";
import { Capacitor } from "@capacitor/core";

const BuyExamForm = () => {
  const [charges, setCharges] = useState("");
  const [showCharges, setShowCharges] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [plan, setPlan] = useState(null);
  const user = useSelector(state => state.user.user)
  const platform = Capacitor.getPlatform();
  const {
    handleSubmit,
    register,
    watch,
    setValue,
    formState: { errors },
  } = useForm();

  const navigate = useNavigate();

  async function getPlans() {
    const { data, err } = await GetRequest({
      url: "/api/exampin-details",
      setState: setIsLoading,
    });

    if (err) {
      toast.error(err.message);
      return;
    }
    setPlans(data);
  }

  async function authenticateWithBiometrics() {
    const result = await NativeBiometric.isAvailable();

    if (!result.isAvailable) {
      toast.warning("Biometric authentication is not available on this device. Kindly enable biometrics in your settings.");
    }

    const verified = await NativeBiometric.verifyIdentity({
      reason: "Authorize Transaction",
      title: "Result checker pin Purchase",
      subtitle: "Please authenticate to proceed with this transaction.",
    }).then(() => true)
      .catch(() => false);

    if (verified) {
      setValue("pin", user.pin.toString())
      document.querySelector("#sub").click()
    } else {
      toast.error("Authentication failed. Please try again.");
    }
  }

  useEffect(() => {
    getPlans();
  }, []);

  if (plans.length === 0)
    return (
      <div className="mt-20">
        <Unavailable />
      </div>
    );

  const onSubmit = async (data) => {
    // console.log(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <section className="mt-2 grid grid-cols-1 gap-[20px]">
        <div className="col-span-1">
          <label htmlFor="plan" className="text-sm font-semibold">
            Exam Type
          </label>

          <Select
            onValueChange={(value) => {
              setPlan(plans.find((p) => p.id == value));
              setValue("plan", value);
            }}
            value={watch("plan") ? Number(watch("plan")) : undefined}
          >
            <SelectTrigger className="h-[2.8rem] w-full">
              <SelectValue placeholder="Please select your exam type" />
            </SelectTrigger>
            <SelectContent>
              {plans.map((plan) => (
                <SelectItem key={plan.id} value={plan.id}>
                  {plan.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div />

          {errors.plan && (
            <span className="text-xs font-medium text-red-500">
              {errors.plan.message}
            </span>
          )}
        </div>

        <div className="col-span-1">
          <label htmlFor="qty" className="text-sm font-semibold">
            Quantity
          </label>

          <div className="dark:border-darkBorder dark:bg-darkInput mt-1 flex items-center gap-x-3 rounded-md border bg-gray-100 p-2 py-2.5">
            <input
              type="number"
              {...register("qty", {
                required: "Quantity is required",
                onChange: (e) => {
                  if (!plan) return "No plan selected";
                  const value = parseInt(e.target.value, 10);
                  const chargePerUnit = parseInt(plan.price, 10);
                  if (value <= 5) {
                    setShowCharges(true);
                    const calculatedCharges = chargePerUnit * value;
                    setCharges(calculatedCharges);
                    return true;
                  } else {
                    setShowCharges(false);
                    return false;
                  }
                },
                max: {
                  value: 5,
                  message: "Maximum quantity is 5",
                },
              })}
              className="w-full border-none bg-transparent text-sm outline-none"
              placeholder="Enter quantity"
            />
          </div>

          {errors.qty && (
            <span className="text-xs font-medium text-red-500">
              {errors.qty.message}
            </span>
          )}
        </div>
        {showCharges && (
          <div className="col-span-1">
            <label
              htmlFor="charges"
              className="text-sm font-semibold text-gray-500"
            >
              Charges
            </label>
            <div className="dark:border-darkBorder dark:bg-darkInput mt-1 flex items-center gap-x-3 rounded-md border bg-gray-100 p-2 py-2.5">
              <input
                type="text"
                value={charges}
                {...register("charges")}
                readOnly
                className="w-full border-none bg-transparent text-sm text-gray-500 outline-none"
              />
            </div>
          </div>
        )}
        <div className="col-span-1">
          <label htmlFor="pin" className="text-sm font-semibold">
            Transaction Pin
          </label>
          <div className="flex gap-3 items-center">
            <div className="dark:border-darkBorder dark:bg-darkInput mt-1 flex items-center gap-x-3 rounded-md border bg-gray-100 p-2 py-2.5">
              <input
                type="password"
                {...register("pin", {
                  required: "Pin is required",
                  pattern: {
                    value: /^[0-9]+$/,
                    message: "Please enter numbers only",
                  },
                })}
                autoComplete="off"
                inputMode="numeric"
                className="w-full border-none bg-transparent text-sm outline-none"
                placeholder="Enter pin"
              />
            </div>
            {platform != "web" && <Button onClick={authenticateWithBiometrics} type="button" className={"h-[40px] text-white"}>
              {platform == "android" ? <Fingerprint size={30} /> : <ScanFace size={30} />} Use Biometrics
            </Button>}
          </div>

          {errors.pin && (
            <span className="text-xs font-medium text-red-500">
              {errors.pin.message}
            </span>
          )}
        </div>

        <button
          type="submit"
          id="sub"
          disabled={isLoading}
          className={cn(
            "btn mt-2 w-full rounded-md py-3 font-semibold text-white disabled:pointer-events-none disabled:bg-gray-600",
            {
              "bg-primary": isLoading,
            },
          )}
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="animate-spin" />
              Purchasing...
            </span>
          ) : (
            "Purchase"
          )}
        </button>
      </section>
    </form>
  );
};

export default BuyExamForm;
